import { useEffect } from 'react';

export default function ConfirmDialog({ 
  isOpen, 
  title = 'Confirmation', 
  message, 
  confirmText = 'Confirmer', 
  cancelText = 'Annuler',
  onConfirm,
  onCancel,
  type = 'danger' // danger, warning, info
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const styles = {
    danger: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'text-red-900',
      message: 'text-red-700',
      confirm: 'bg-red-600 hover:bg-red-700 text-white',
      cancel: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      title: 'text-amber-900',
      message: 'text-amber-700',
      confirm: 'bg-amber-600 hover:bg-amber-700 text-white',
      cancel: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'text-blue-900',
      message: 'text-blue-700',
      confirm: 'bg-blue-600 hover:bg-blue-700 text-white',
      cancel: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }
  };

  const currentStyle = styles[type] || styles.danger;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-md rounded-lg border shadow-xl ${currentStyle.bg} ${currentStyle.border} p-6`}>
          {/* Icon */}
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white mx-auto mb-4">
            {type === 'danger' && (
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {type === 'warning' && (
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {type === 'info' && (
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h3 className={`text-lg font-semibold mb-2 ${currentStyle.title}`}>
              {title}
            </h3>
            <p className={`text-sm ${currentStyle.message}`}>
              {message}
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className={`flex-1 px-4 py-2 rounded-lg border transition-colors font-medium ${currentStyle.cancel}`}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${currentStyle.confirm}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
